
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { StudentLayout } from "@/components/student/layout";
import { toast } from "sonner";
import { useLessonCompletions } from "@/hooks/useLessonCompletions";
import { useMilestoneAchievements } from '@/hooks/useMilestoneAchievements';
import { CourseReviewModal } from '@/components/CourseReviewModal';
import { CourseLoading } from "@/components/course/CourseLoading";
import { performance } from "@/utils/performance";
import { devLog } from "@/utils/devLog";
import { useEnrollments } from "@/hooks/useEnrollments";
import { useAuth } from "@/providers/AuthProvider";
import { fetchCourse } from "@/services/courses";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import CoursePlayerErrorBoundary from "@/components/course/CoursePlayerErrorBoundary";
import { CoursePlayerContent } from "@/components/course/CoursePlayerContent";
import { Enrollment } from "@/types/enrollment";
import { Course, Module, Lesson } from "@/types/course";

// Define a proper type for the course data to fix TypeScript errors
interface CourseData {
  id: string;
  title: string;
  modules?: {
    id: string;
    title: string;
    order: number;
    lessons?: {
      id: string;
      title: string;
      description?: string;
      video_url?: string;
      is_preview?: boolean;
      module_id: string;
      lesson_files?: any[];
      lesson_faqs?: any[];
    }[];
  }[];
  creatorProfile?: {
    username?: string;
    full_name?: string;
  };
  [key: string]: any;
}

const CoursePlayer = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient(); // Access the query client
  const [selectedLesson, setSelectedLesson] = useState<any>(null);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [hasShownReviewModal, setHasShownReviewModal] = useState(false);
  
  // Use ref to track performance measurement state
  const perfMarkRef = useRef<{ mark: ReturnType<typeof performance.start>, active: boolean }>({
    mark: null as any,
    active: false
  });

  // Track component mount for performance
  useEffect(() => {
    const perfMark = performance.start('course-player-render');
    perfMarkRef.current = { mark: perfMark, active: true };
    
    devLog("[CoursePlayer] Component rendering started", { timestamp: new Date().toISOString() });
    
    return () => {
      // Ensure we clean up the performance mark if component unmounts
      if (perfMarkRef.current.active) {
        performance.end(perfMarkRef.current.mark);
        perfMarkRef.current.active = false;
      }
    };
  }, []);

  // Check for cached data first
  const cachedCourseData = courseId ? queryClient.getQueryData<CourseData>(['coursePlayer', courseId]) : null;
  
  // Use unified enrollments hook with userId
  const { enrollments, isLoading: loadingEnrollments } = useEnrollments(user?.id);

  // Memoize the enrollment check to prevent unnecessary re-computation
  const isEnrolled = useMemo(() => {
    if (!courseId || !enrollments) return false;
    const enrollmentsArray = enrollments as Enrollment[];
    return enrollmentsArray.some(enrollment => enrollment.course_id === courseId);
  }, [courseId, enrollments]);

  // Use the lesson completions hook
  const {
    completions,
    completedLessonIds,
    isLessonCompleted,
    markLessonCompleted,
    markLessonIncomplete,
    calculateProgress,
    isLoading: loadingCompletions,
    totalLessons
  } = useLessonCompletions(courseId || '');

  // Use milestone achievements hook
  useMilestoneAchievements(courseId || '');

  // Fetch course details with modules and lessons using the service function
  const { 
    data: courseData, 
    isLoading: loadingCourse,
    isFetching 
  } = useQuery<CourseData>({
    queryKey: ['coursePlayer', courseId],
    queryFn: () => courseId ? fetchCourse(courseId) : Promise.reject("No course ID"),
    enabled: !!courseId,
    staleTime: 10 * 60_000,         // 10 minutes (increased from 5 minutes)
    gcTime: 30 * 60_000,            // 30 minutes (v5 API)
    refetchOnMount: false,          // don't refetch when component mounts
    refetchOnWindowFocus: false,    // don't refetch on window/tab focus
    refetchInterval: false,         // don't periodically refetch
    // Pass the previous data to prevent unnecessary loading state
    placeholderData: cachedCourseData || undefined
  });

  // Get current user data to pass to the review modal
  const { data: userData } = useQuery({
    queryKey: ['currentUserForReview'],
    queryFn: async () => {
      const { data } = await supabase.auth.getUser();
      return data;
    },
  });

  // Check if user has already reviewed this course
  const { data: existingReview } = useQuery({
    queryKey: ['course-review-exists', courseId, user?.id],
    queryFn: async () => {
      if (!user?.id || !courseId) return null;
      
      const { data, error } = await supabase
        .from('course_reviews')
        .select('id')
        .eq('user_id', user.id)
        .eq('course_id', courseId)
        .limit(1);
        
      if (error) {
        console.error("Error checking existing review:", error);
        return null;
      }
      
      return data && data.length > 0 ? data[0] : null;
    },
    enabled: !!user?.id && !!courseId,
  });

  // Set the first lesson as selected when data loads
  useEffect(() => {
    if (courseData?.modules?.length && !selectedLesson) {
      const firstModuleId = courseData.modules[0].id;
      setExpandedModules(new Set([firstModuleId]));
      
      if (courseData.modules[0].lessons?.length) {
        setSelectedLesson(courseData.modules[0].lessons[0]);
      }
    }
  }, [courseData, selectedLesson]);

  // End performance tracking when course data is successfully loaded
  useEffect(() => {
    if (!loadingCourse && !loadingEnrollments && !loadingCompletions && perfMarkRef.current.active) {
      performance.end(perfMarkRef.current.mark, "Data loaded successfully");
      perfMarkRef.current.active = false;
      
      devLog("[CoursePlayer] Component fully rendered with data", { 
        hasCourseData: !!courseData,
        moduleCount: courseData?.modules?.length || 0,
        fromCache: !!cachedCourseData
      });
      setInitialLoadComplete(true);
    }
  }, [loadingCourse, loadingEnrollments, loadingCompletions, courseData, cachedCourseData]);

  // Update course in local storage on every successful load
  useEffect(() => {
    if (courseData && courseId && initialLoadComplete) {
      try {
        localStorage.setItem(`course_${courseId}`, JSON.stringify({
          ...courseData,
          cachedAt: new Date().toISOString()
        }));
        devLog("[CoursePlayer] Updated course cache in localStorage", { courseId });
      } catch (err) {
        // Ignore storage errors
      }
    }
  }, [courseData, courseId, initialLoadComplete]);

  const toggleModule = useCallback((moduleId: string) => {
    setExpandedModules(prev => {
      const newExpandedModules = new Set(prev);
      if (newExpandedModules.has(moduleId)) {
        newExpandedModules.delete(moduleId);
      } else {
        newExpandedModules.add(moduleId);
      }
      return newExpandedModules;
    });
  }, []);

  const goToNextLesson = useCallback(() => {
    if (!courseData?.modules) return false;
    
    let foundCurrent = false;
    let nextLesson = null;
    
    for (const module of courseData.modules) {
      if (!module.lessons || module.lessons.length === 0) continue;
      
      for (const lesson of module.lessons) {
        if (foundCurrent) {
          nextLesson = lesson;
          break;
        }
        
        if (lesson.id === selectedLesson?.id) {
          foundCurrent = true;
        }
      }
      
      if (nextLesson) break;
    }
    
    if (nextLesson) {
      setSelectedLesson(nextLesson);
      const moduleId = nextLesson.module_id;
      setExpandedModules(prev => {
        const newSet = new Set(prev);
        newSet.add(moduleId);
        return newSet;
      });
      toast.success("Moving to next lesson");
      return true;
    } else {
      toast.info("This is the last lesson in the course");
      return false;
    }
  }, [courseData?.modules, selectedLesson?.id]);

  // Handle review modal display
  useEffect(() => {
    const progress = calculateProgress();
    
    // Only show review modal if:
    // 1. Course is completed (100%)
    // 2. User hasn't already reviewed the course
    // 3. We haven't shown the modal yet in this session
    if (progress === 100 && 
        completedLessonIds.size === totalLessons && 
        totalLessons > 0 && 
        !existingReview && 
        !hasShownReviewModal) {
      setIsReviewModalOpen(true);
      setHasShownReviewModal(true);
    }
  }, [completedLessonIds.size, totalLessons, calculateProgress, existingReview, hasShownReviewModal]);

  // Handle review submission
  const handleReviewSubmitted = () => {
    // Close the modal
    setIsReviewModalOpen(false);
    // Invalidate the existing review query to refresh the state
    queryClient.invalidateQueries({ queryKey: ['course-review-exists', courseId, user?.id] });
    // Show success message
    toast.success("Thank you for your review!");
  };

  // Only show loading state on initial load, not when switching courses
  const isInitialLoading = (loadingCourse || loadingEnrollments || loadingCompletions) && !cachedCourseData;

  // Show loading state while data is being fetched
  if (isInitialLoading) {
    // Make sure to end the performance measurement before returning early
    if (perfMarkRef.current.active) {
      performance.end(perfMarkRef.current.mark, "Returned early due to loading state");
      perfMarkRef.current.active = false;
    }
    return (
      <StudentLayout>
        <CourseLoading />
      </StudentLayout>
    );
  }

  // If we have no course data after loading completes
  if (!courseData) {
    // Make sure to end the performance measurement before returning early
    if (perfMarkRef.current.active) {
      performance.end(perfMarkRef.current.mark, "Returned early due to missing course data");
      perfMarkRef.current.active = false;
    }
    return (
      <StudentLayout>
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold mb-4">Course not found</h2>
          <p className="text-muted-foreground mb-6">
            The course you're looking for couldn't be found.
          </p>
          <Button onClick={() => navigate('/my-learning')}>
            Back to My Learning
          </Button>
        </Card>
      </StudentLayout>
    );
  }

  const progressPercentage = calculateProgress();

  return (
    <StudentLayout 
      title={courseData.title} 
      description="Course Curriculum"
    >
      <CoursePlayerErrorBoundary>
        {isFetching && !isInitialLoading && (
          <div className="absolute top-2 right-2 text-xs text-muted-foreground bg-background/80 px-2 py-1 rounded-md">
            Updating in background...
          </div>
        )}
        <CoursePlayerContent
          courseData={courseData}
          selectedLesson={selectedLesson}
          setSelectedLesson={setSelectedLesson}
          expandedModules={expandedModules}
          toggleModule={toggleModule}
          isLessonCompleted={isLessonCompleted}
          markLessonCompleted={markLessonCompleted}
          markLessonIncomplete={markLessonIncomplete}
          courseId={courseId || ''}
          goToNextLesson={goToNextLesson}
          progressPercentage={progressPercentage}
        />
      </CoursePlayerErrorBoundary>

      <CourseReviewModal
        courseId={courseId || ''}
        userId={userData?.user?.id || ''}
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        onReviewSubmitted={handleReviewSubmitted}
      />
    </StudentLayout>
  );
};

export default CoursePlayer;
